import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import SignaturePad from '@/components/SignaturePad';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  CheckCircle, Clock, AlertTriangle, FileText, Download, Save, Loader2,
  User, Phone, Mail, MapPin, Briefcase, Heart, Shield, Baby, Sun, Bug,
  Stethoscope, AlertCircle, ClipboardList, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

// Form component definitions
const FORM_CONFIGS = {
  enrollment: {
    title: 'Child Care Enrollment',
    dcfCode: 'DCF-F-CFS0062',
    icon: ClipboardList,
    color: 'bg-blue-500'
  },
  health_report: {
    title: 'Child Health Report',
    dcfCode: 'DCF-F-CFS0060-E',
    icon: Heart,
    color: 'bg-red-500'
  },
  intake_under2: {
    title: 'Intake for Child Under 2',
    dcfCode: 'DCF-F-CFS0061',
    icon: Baby,
    color: 'bg-purple-500'
  },
  health_history: {
    title: 'Health History & Emergency Care Plan',
    dcfCode: 'DCF-F-CFS2345-E',
    icon: Stethoscope,
    color: 'bg-green-500'
  },
  payment_agreement: {
    title: 'Provider / Parent Payment Agreement',
    dcfCode: 'DCF-F-CFS5224',
    icon: DollarSign,
    color: 'bg-amber-500'
  }
};

const ParentEnrollmentPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkData, setLinkData] = useState(null);
  const [activeForm, setActiveForm] = useState(null);
  const [selectedChildId, setSelectedChildId] = useState(null);
  const [formData, setFormData] = useState({}); // keyed by `${childId}::${formType}`
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    validateToken();
  }, [token]);

  const dataKey = (childId, formType) => `${childId}::${formType}`;

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API}/enrollment/validate/${token}`);
      setLinkData(response.data);

      // Determine the initial child + form to open
      const roster = response.data.children || [{ id: response.data.child_id }];
      const primaryChildId = roster[0].id;
      setSelectedChildId(primaryChildId);

      // Pick the first form this child hasn't done yet (fall back to first form)
      const childCompleted = roster[0].forms_completed || [];
      const firstIncomplete = response.data.forms_to_complete.find(
        f => !childCompleted.includes(f)
      );
      const initialForm = firstIncomplete || response.data.forms_to_complete[0];
      if (initialForm) {
        setActiveForm(initialForm);
        await loadFormData(initialForm, primaryChildId);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired link');
    } finally {
      setLoading(false);
    }
  };

  const loadFormData = async (formType, childId = null) => {
    const cid = childId || selectedChildId;
    if (!cid) return;
    try {
      const response = await axios.get(
        `${API}/enrollment/form-data/${token}/${formType}?child_id=${cid}`
      );
      setFormData(prev => ({ ...prev, [dataKey(cid, formType)]: response.data }));
    } catch (err) {
      console.error('Error loading form data:', err);
    }
  };

  const handleFormChange = (formType) => {
    setActiveForm(formType);
    if (!formData[dataKey(selectedChildId, formType)]) {
      loadFormData(formType, selectedChildId);
    }
  };

  const handleChildChange = (newChildId) => {
    setSelectedChildId(newChildId);
    if (activeForm && !formData[dataKey(newChildId, activeForm)]) {
      loadFormData(activeForm, newChildId);
    }
  };

  const handleSaveForm = async () => {
    if (!activeForm || !selectedChildId) return;

    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/enrollment/save-form/${token}/${activeForm}?child_id=${selectedChildId}`,
        formData[dataKey(selectedChildId, activeForm)]
      );

      // Update link data with new completion status
      setLinkData(prev => ({
        ...prev,
        forms_completed: response.data.forms_completed,
        // Sync per-child completion so the child selector reflects progress
        children: (prev?.children || []).map(c =>
          c.id === selectedChildId
            ? { ...c, forms_completed: (response.data.forms_completed_by_child || {})[selectedChildId] || c.forms_completed }
            : c
        ),
      }));

      if (response.data.is_complete) {
        toast.success('Form saved and marked as complete!');
      } else {
        toast.success('Form saved. Add your signature to complete it.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async (formType) => {
    try {
      const response = await axios.get(
        `${API}/enrollment/download-pdf/${token}/${formType}?child_id=${selectedChildId || ''}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const config = FORM_CONFIGS[formType];
      link.download = `${config.dcfCode}_${linkData.child_name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const updateFormField = (field, value) => {
    const k = dataKey(selectedChildId, activeForm);
    setFormData(prev => ({
      ...prev,
      [k]: {
        ...prev[k],
        [field]: value
      }
    }));
  };

  const updateNestedField = (section, field, value) => {
    const k = dataKey(selectedChildId, activeForm);
    setFormData(prev => ({
      ...prev,
      [k]: {
        ...prev[k],
        [section]: {
          ...prev[k]?.[section],
          [field]: value
        }
      }
    }));
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-white">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-gray-600">Validating your enrollment link...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-white p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <CardTitle className="text-red-600">Link Error</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-gray-500 mb-4">
              Please contact the childcare center for assistance or request a new enrollment link.
            </p>
            <Link to="/">
              <Button variant="outline">Return to Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const completedCount = linkData.forms_completed.length;
  const totalCount = linkData.forms_to_complete.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{linkData.center_name}</h1>
              <p className="text-sm text-gray-500">Parent Enrollment Portal</p>
            </div>
            <Badge variant="outline" className="text-amber-600 border-amber-300">
              <Clock className="w-3 h-3 mr-1" />
              Expires: {new Date(linkData.expires_at).toLocaleDateString()}
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Welcome Card */}
        <Card className="mb-8 border-amber-200 bg-amber-50/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl">
                  Welcome{linkData.parent_name ? `, ${linkData.parent_name}` : ''}!
                </CardTitle>
                <CardDescription className="text-base mt-1">
                  Complete the enrollment forms for <strong>{linkData.child_name}</strong>
                </CardDescription>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500 mb-1">Progress</p>
                <p className="text-2xl font-bold text-amber-600">{completedCount}/{totalCount}</p>
              </div>
            </div>
            <Progress value={progressPercent} className="mt-4 h-2" />
          </CardHeader>
        </Card>

        {/* Child selector (multi-child links) */}
        {Array.isArray(linkData.children) && linkData.children.length > 1 && (
          <Card className="mb-6 border-2 border-primary/30 bg-primary/5" data-testid="child-selector-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Baby className="w-4 h-4 text-primary" />
                <p className="font-semibold text-sm">Filling forms for</p>
                <span className="text-xs text-muted-foreground">
                  ({linkData.children.length} children on this link)
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {linkData.children.map(c => {
                  const total = linkData.forms_to_complete.length;
                  const done = (c.forms_completed || []).length;
                  const isCurrent = c.id === selectedChildId;
                  const allDone = done === total && total > 0;
                  return (
                    <button
                      key={c.id}
                      onClick={() => handleChildChange(c.id)}
                      className={`px-3 py-2 rounded-lg text-sm border transition-all flex items-center gap-2 ${
                        isCurrent
                          ? 'bg-primary text-white border-primary shadow'
                          : allDone
                          ? 'bg-green-50 border-green-300 text-green-800'
                          : 'bg-white border-gray-200 hover:border-primary'
                      }`}
                      data-testid={`child-tab-${c.id}`}
                    >
                      {allDone && <CheckCircle className="w-4 h-4" />}
                      <span className="font-medium">{c.first_name} {c.last_name}</span>
                      <span className={`text-xs ${isCurrent ? 'text-white/80' : 'text-gray-500'}`}>
                        {done}/{total}
                      </span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Forms Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Form Selection Sidebar */}
          <div className="lg:col-span-1 space-y-3">
            <h3 className="font-semibold text-gray-700 mb-3">Required Forms</h3>
            {linkData.forms_to_complete.map(formType => {
              const config = FORM_CONFIGS[formType];
              // For multi-child links, "complete" means the currently-selected
              // child has completed it. Fall back to global for single-child links.
              const selectedChild = (linkData.children || []).find(c => c.id === selectedChildId);
              const childCompleted = selectedChild?.forms_completed || linkData.forms_completed || [];
              const isComplete = childCompleted.includes(formType);
              const isActive = activeForm === formType;
              const Icon = config.icon;
              
              return (
                <button
                  key={formType}
                  onClick={() => handleFormChange(formType)}
                  className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                    isActive 
                      ? 'border-amber-500 bg-amber-50 shadow-md' 
                      : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                  }`}
                  data-testid={`form-select-${formType}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${isComplete ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {isComplete ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <Icon className="w-5 h-5 text-gray-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{config.title}</p>
                      <p className="text-xs text-gray-400">{config.dcfCode}</p>
                      {isComplete && (
                        <Badge className="mt-1 bg-green-100 text-green-700 text-xs">Complete</Badge>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}

            {/* All Complete Message */}
            {completedCount === totalCount && (
              <Card className="bg-green-50 border-green-200 mt-4">
                <CardContent className="p-4 text-center">
                  <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <p className="font-semibold text-green-700">All Forms Complete!</p>
                  <p className="text-xs text-green-600 mt-1">
                    Thank you for completing the enrollment.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Form Content Area */}
          <div className="lg:col-span-3">
            {activeForm && (
              <Card className="shadow-lg">
                <CardHeader className="border-b bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {React.createElement(FORM_CONFIGS[activeForm].icon, { 
                        className: "w-6 h-6 text-amber-600" 
                      })}
                      <div>
                        <CardTitle>{FORM_CONFIGS[activeForm].title}</CardTitle>
                        <CardDescription>{FORM_CONFIGS[activeForm].dcfCode}</CardDescription>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleDownloadPDF(activeForm)}
                        data-testid="download-pdf-btn"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={handleSaveForm}
                        disabled={saving}
                        data-testid="save-form-btn"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-1" />
                        )}
                        Save
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <ScrollArea className="h-[600px] pr-4">
                    {activeForm === 'enrollment' && (
                      <EnrollmentFormContent 
                        data={formData[dataKey(selectedChildId, 'enrollment')] || {}}
                        updateField={updateFormField}
                        updateNested={updateNestedField}
                      />
                    )}
                    {activeForm === 'health_history' && (
                      <HealthHistoryFormContent 
                        data={formData[dataKey(selectedChildId, 'health_history')] || {}}
                        updateField={updateFormField}
                        updateNested={updateNestedField}
                      />
                    )}
                    {activeForm === 'health_report' && (
                      <HealthReportFormContent 
                        data={formData[dataKey(selectedChildId, 'health_report')] || {}}
                        updateField={updateFormField}
                        updateNested={updateNestedField}
                      />
                    )}
                    {activeForm === 'intake_under2' && (
                      <IntakeUnder2FormContent 
                        data={formData[dataKey(selectedChildId, 'intake_under2')] || {}}
                        updateField={updateFormField}
                        updateNested={updateNestedField}
                      />
                    )}
                    {activeForm === 'payment_agreement' && (
                      <PaymentAgreementFormContent
                        data={formData[dataKey(selectedChildId, 'payment_agreement')] || {}}
                        updateField={updateFormField}
                      />
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-100 border-t py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-500">
            These forms comply with Wisconsin DCF regulations. 
            Your information is protected and secure.
          </p>
        </div>
      </footer>
    </div>
  );
};

// ==================== FORM CONTENT COMPONENTS ====================

const EnrollmentFormContent = ({ data, updateField, updateNested }) => {
  return (
    <div className="space-y-6">
      {/* Child Information */}
      <div>
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-amber-600" />
          Child Information
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>First Name *</Label>
            <Input 
              value={data.child_first_name || ''} 
              onChange={(e) => updateField('child_first_name', e.target.value)}
              placeholder="First name"
            />
          </div>
          <div className="space-y-2">
            <Label>Last Name *</Label>
            <Input 
              value={data.child_last_name || ''} 
              onChange={(e) => updateField('child_last_name', e.target.value)}
              placeholder="Last name"
            />
          </div>
          <div className="space-y-2">
            <Label>MI</Label>
            <Input 
              value={data.child_middle_initial || ''} 
              onChange={(e) => updateField('child_middle_initial', e.target.value)}
              maxLength={1}
              className="w-16"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="space-y-2">
            <Label>Date of Birth *</Label>
            <Input 
              type="date"
              value={data.child_birthdate || ''} 
              onChange={(e) => updateField('child_birthdate', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>First Day of Attendance</Label>
            <Input 
              type="date"
              value={data.first_day_attendance || ''} 
              onChange={(e) => updateField('first_day_attendance', e.target.value)}
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Parent/Guardian A */}
      <div>
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-amber-600" />
          Parent/Guardian A *
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Full Name *</Label>
            <Input 
              value={data.parent_guardian_a?.name || ''} 
              onChange={(e) => updateNested('parent_guardian_a', 'name', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Relationship *</Label>
            <Input 
              value={data.parent_guardian_a?.relationship || ''} 
              onChange={(e) => updateNested('parent_guardian_a', 'relationship', e.target.value)}
              placeholder="Mother, Father, Guardian"
            />
          </div>
          <div className="space-y-2">
            <Label>Phone *</Label>
            <Input 
              value={data.parent_guardian_a?.home_cell_phone || ''} 
              onChange={(e) => updateNested('parent_guardian_a', 'home_cell_phone', e.target.value)}
              placeholder="(555) 123-4567"
            />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input 
              type="email"
              value={data.parent_guardian_a?.email || ''} 
              onChange={(e) => updateNested('parent_guardian_a', 'email', e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-2 mt-4">
          <Label>Home Address</Label>
          <Input 
            value={data.parent_guardian_a?.home_address || ''} 
            onChange={(e) => updateNested('parent_guardian_a', 'home_address', e.target.value)}
            placeholder="Street, City, State, ZIP"
          />
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="space-y-2">
            <Label>Employer</Label>
            <Input 
              value={data.parent_guardian_a?.employer || ''} 
              onChange={(e) => updateNested('parent_guardian_a', 'employer', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Work Phone</Label>
            <Input 
              value={data.parent_guardian_a?.work_phone || ''} 
              onChange={(e) => updateNested('parent_guardian_a', 'work_phone', e.target.value)}
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Emergency Contact */}
      <div>
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Phone className="w-5 h-5 text-red-500" />
          Emergency Contact
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input 
              value={data.emergency_contact?.name || ''} 
              onChange={(e) => updateNested('emergency_contact', 'name', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Relationship *</Label>
            <Input 
              value={data.emergency_contact?.relationship || ''} 
              onChange={(e) => updateNested('emergency_contact', 'relationship', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Phone *</Label>
            <Input 
              value={data.emergency_contact?.home_cell_phone || ''} 
              onChange={(e) => updateNested('emergency_contact', 'home_cell_phone', e.target.value)}
            />
          </div>
        </div>
        <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <Label className="font-medium">Is this person authorized to pick up the child?</Label>
          <RadioGroup
            value={data.emergency_contact?.authorized_pickup === true ? 'yes' : data.emergency_contact?.authorized_pickup === false ? 'no' : ''}
            onValueChange={(v) => updateNested('emergency_contact', 'authorized_pickup', v === 'yes')}
            className="flex gap-4 mt-2"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="yes" id="ec-pickup-yes" />
              <Label htmlFor="ec-pickup-yes">Yes</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="no" id="ec-pickup-no" />
              <Label htmlFor="ec-pickup-no">No</Label>
            </div>
          </RadioGroup>
        </div>
      </div>

      <Separator />

      {/* Physician */}
      <div>
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Heart className="w-5 h-5 text-red-500" />
          Physician/Medical Facility
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Physician Name</Label>
            <Input 
              value={data.physician?.name || ''} 
              onChange={(e) => updateNested('physician', 'name', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Phone</Label>
            <Input 
              value={data.physician?.phone || ''} 
              onChange={(e) => updateNested('physician', 'phone', e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-2 mt-4">
          <Label>Address</Label>
          <Input 
            value={data.physician?.address || ''} 
            onChange={(e) => updateNested('physician', 'address', e.target.value)}
          />
        </div>
      </div>

      <Separator />

      {/* Authorizations */}
      <div>
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-500" />
          Authorizations
        </h3>
        <div className="space-y-4">
          <div className="p-4 border rounded-lg">
            <Label className="font-medium">Emergency Medical Care</Label>
            <p className="text-sm text-gray-500 mb-2">I authorize emergency medical care for my child if I cannot be reached.</p>
            <RadioGroup
              value={data.authorizations?.emergency_care === true ? 'yes' : data.authorizations?.emergency_care === false ? 'no' : ''}
              onValueChange={(v) => updateNested('authorizations', 'emergency_care', v === 'yes')}
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yes" id="auth-emergency-yes" />
                <Label htmlFor="auth-emergency-yes">Yes</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="no" id="auth-emergency-no" />
                <Label htmlFor="auth-emergency-no">No</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="p-4 border rounded-lg">
            <Label className="font-medium">Field Trip Permission</Label>
            <p className="text-sm text-gray-500 mb-2">I authorize my child to participate in field trips.</p>
            <RadioGroup
              value={data.authorizations?.field_trips === true ? 'yes' : data.authorizations?.field_trips === false ? 'no' : ''}
              onValueChange={(v) => updateNested('authorizations', 'field_trips', v === 'yes')}
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yes" id="auth-trips-yes" />
                <Label htmlFor="auth-trips-yes">Yes</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="no" id="auth-trips-no" />
                <Label htmlFor="auth-trips-no">No</Label>
              </div>
            </RadioGroup>
          </div>
        </div>
      </div>

      <Separator />

      {/* Signature */}
      <div className="p-6 bg-amber-50 rounded-lg border border-amber-200">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-amber-600" />
          Parent/Guardian Signature
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Signature (Type Full Name) *</Label>
            <Input 
              value={data.parent_signature || ''} 
              onChange={(e) => updateField('parent_signature', e.target.value)}
              placeholder="Type your full legal name"
              className="font-cursive text-lg"
            />
            <p className="text-xs text-gray-500">
              By typing your name, you acknowledge this as your electronic signature.
            </p>
          </div>
          <div className="space-y-2">
            <Label>Date *</Label>
            <Input 
              type="date"
              value={data.signature_date || ''} 
              onChange={(e) => updateField('signature_date', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const HealthHistoryFormContent = ({ data, updateField, updateNested }) => {
  return (
    <div className="space-y-6">
      {/* Child Information */}
      <div>
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-amber-600" />
          Child Information
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>First Name</Label>
            <Input 
              value={data.child_first_name || ''} 
              onChange={(e) => updateField('child_first_name', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Last Name</Label>
            <Input 
              value={data.child_last_name || ''} 
              onChange={(e) => updateField('child_last_name', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Date of Birth</Label>
            <Input 
              type="date"
              value={data.child_birthdate || ''} 
              onChange={(e) => updateField('child_birthdate', e.target.value)}
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Medical Conditions */}
      <div>
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Medical Conditions
        </h3>
        <p className="text-sm text-gray-500 mb-4">Check any conditions that apply:</p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { key: 'no_specific_condition', label: 'No specific medical condition' },
            { key: 'asthma', label: 'Asthma' },
            { key: 'diabetes', label: 'Diabetes' },
            { key: 'epilepsy_seizure', label: 'Epilepsy / Seizure disorder' },
            { key: 'food_allergies', label: 'Food allergies' },
            { key: 'non_food_allergies', label: 'Non-food allergies' }
          ].map(condition => (
            <label key={condition.key} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer">
              <Checkbox 
                checked={data.conditions?.[condition.key] || false}
                onCheckedChange={(v) => updateNested('conditions', condition.key, v)}
              />
              <span>{condition.label}</span>
            </label>
          ))}
        </div>
        {data.conditions?.food_allergies && (
          <div className="mt-4 space-y-2">
            <Label>Specify food allergies</Label>
            <Input 
              value={data.conditions?.food_allergies_specify || ''} 
              onChange={(e) => updateNested('conditions', 'food_allergies_specify', e.target.value)}
              placeholder="List specific food allergies"
            />
          </div>
        )}
      </div>

      <Separator />

      {/* Emergency Care Plan */}
      <div>
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Stethoscope className="w-5 h-5 text-red-500" />
          Emergency Care Plan
        </h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Triggers that may cause problems</Label>
            <Textarea 
              value={data.care_plan?.triggers || ''} 
              onChange={(e) => updateNested('care_plan', 'triggers', e.target.value)}
              placeholder="Describe any triggers..."
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label>Signs or symptoms to watch for</Label>
            <Textarea 
              value={data.care_plan?.signs_symptoms || ''} 
              onChange={(e) => updateNested('care_plan', 'signs_symptoms', e.target.value)}
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label>Steps staff should follow</Label>
            <Textarea 
              value={data.care_plan?.provider_steps || ''} 
              onChange={(e) => updateNested('care_plan', 'provider_steps', e.target.value)}
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label>When to call parents</Label>
            <Textarea 
              value={data.care_plan?.when_to_call_parents || ''} 
              onChange={(e) => updateNested('care_plan', 'when_to_call_parents', e.target.value)}
              rows={2}
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Signature */}
      <div className="p-6 bg-amber-50 rounded-lg border border-amber-200">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-amber-600" />
          Parent/Guardian Signature
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Signature (Type Full Name) *</Label>
            <Input 
              value={data.parent_signature || ''} 
              onChange={(e) => updateField('parent_signature', e.target.value)}
              placeholder="Type your full legal name"
              className="font-cursive text-lg"
            />
          </div>
          <div className="space-y-2">
            <Label>Date *</Label>
            <Input 
              type="date"
              value={data.signature_date || ''} 
              onChange={(e) => updateField('signature_date', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const HealthReportFormContent = ({ data, updateField, updateNested }) => {
  return (
    <div className="space-y-6">
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
        <strong>Note:</strong> This form should be completed by your child's health care provider.
        You may fill in the basic information and have your provider complete the rest.
      </div>

      {/* Child Information */}
      <div>
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-amber-600" />
          Child Information
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Child's Name</Label>
            <Input 
              value={data.child_name || `${data.child_first_name || ''} ${data.child_last_name || ''}`} 
              onChange={(e) => updateField('child_name', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Date of Birth</Label>
            <Input 
              type="date"
              value={data.child_birthdate || ''} 
              onChange={(e) => updateField('child_birthdate', e.target.value)}
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Health Information */}
      <div>
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Heart className="w-5 h-5 text-red-500" />
          Health Information
        </h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Special Health Concerns</Label>
            <Textarea 
              value={data.special_concerns || ''} 
              onChange={(e) => updateField('special_concerns', e.target.value)}
              placeholder="Any health concerns the childcare provider should know about"
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label>Allergies</Label>
            <Textarea 
              value={data.allergies || ''} 
              onChange={(e) => updateField('allergies', e.target.value)}
              placeholder="List any known allergies"
              rows={2}
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Provider Signature (to be completed by health provider) */}
      <div className="p-6 bg-gray-50 rounded-lg border">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Stethoscope className="w-5 h-5 text-blue-500" />
          Health Care Provider Section
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          To be completed by your child's health care provider
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Provider Name</Label>
            <Input 
              value={data.provider_name || ''} 
              onChange={(e) => updateField('provider_name', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Exam Date</Label>
            <Input 
              type="date"
              value={data.exam_date || ''} 
              onChange={(e) => updateField('exam_date', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Provider Signature</Label>
            <Input 
              value={data.provider_signature || ''} 
              onChange={(e) => updateField('provider_signature', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Date Signed</Label>
            <Input 
              type="date"
              value={data.signature_date || ''} 
              onChange={(e) => updateField('signature_date', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const IntakeUnder2FormContent = ({ data, updateField, updateNested }) => {
  return (
    <div className="space-y-6">
      <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg text-sm">
        <strong>Important:</strong> This form is required for children under 2 years old and helps us 
        provide the best care for your infant/toddler.
      </div>

      {/* Child Information */}
      <div>
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Baby className="w-5 h-5 text-purple-600" />
          Child Information
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Child's Name</Label>
            <Input 
              value={data.child_name || `${data.child_first_name || ''} ${data.child_last_name || ''}`} 
              onChange={(e) => updateField('child_name', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Date of Birth</Label>
            <Input 
              type="date"
              value={data.child_birthdate || ''} 
              onChange={(e) => updateField('child_birthdate', e.target.value)}
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Feeding */}
      <div>
        <h3 className="font-semibold text-lg mb-4">Feeding Information</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Feeding Schedule</Label>
            <Textarea 
              value={data.feeding?.schedule || ''} 
              onChange={(e) => updateNested('feeding', 'schedule', e.target.value)}
              placeholder="Describe your child's typical feeding schedule"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label>Food Types</Label>
            <div className="grid grid-cols-2 gap-2">
              {['Breast milk', 'Formula', 'Baby food', 'Table food'].map(type => (
                <label key={type} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox 
                    checked={data.feeding?.types?.includes(type) || false}
                    onCheckedChange={(checked) => {
                      const types = data.feeding?.types || [];
                      const newTypes = checked 
                        ? [...types, type]
                        : types.filter(t => t !== type);
                      updateNested('feeding', 'types', newTypes);
                    }}
                  />
                  <span>{type}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Food Allergies or Restrictions</Label>
            <Input 
              value={data.feeding?.allergies || ''} 
              onChange={(e) => updateNested('feeding', 'allergies', e.target.value)}
              placeholder="List any food allergies or dietary restrictions"
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Sleep */}
      <div>
        <h3 className="font-semibold text-lg mb-4">Sleep Information</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Sleep Schedule</Label>
            <Textarea 
              value={data.sleep?.schedule || ''} 
              onChange={(e) => updateNested('sleep', 'schedule', e.target.value)}
              placeholder="Describe nap times and sleep patterns"
              rows={2}
            />
          </div>
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <Label className="font-medium text-red-700">Sleep Position (for infants under 1 year)</Label>
            <p className="text-sm text-red-600 mb-2">
              Per safe sleep guidelines, infants must be placed on their back to sleep.
            </p>
            <RadioGroup
              value={data.sleep?.position || ''}
              onValueChange={(v) => updateNested('sleep', 'position', v)}
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="back" id="sleep-back" />
                <Label htmlFor="sleep-back">Back (required)</Label>
              </div>
            </RadioGroup>
          </div>
          <div className="space-y-2">
            <Label>Comfort Items</Label>
            <Input 
              value={data.sleep?.comfort_items || ''} 
              onChange={(e) => updateNested('sleep', 'comfort_items', e.target.value)}
              placeholder="Pacifier, blanket, etc."
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Signature */}
      <div className="p-6 bg-amber-50 rounded-lg border border-amber-200">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-amber-600" />
          Parent/Guardian Signature
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Signature (Type Full Name) *</Label>
            <Input 
              value={data.parent_signature || ''} 
              onChange={(e) => updateField('parent_signature', e.target.value)}
              placeholder="Type your full legal name"
              className="font-cursive text-lg"
            />
          </div>
          <div className="space-y-2">
            <Label>Date *</Label>
            <Input 
              type="date"
              value={data.signature_date || ''} 
              onChange={(e) => updateField('signature_date', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};


// ==================== PAYMENT AGREEMENT (DCF-F-CFS5224) ====================

const PolicyReadOnly = ({ label, content }) => (
  <div className="rounded-lg border bg-amber-50/40 p-3">
    <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide mb-1">{label}</p>
    <p className="text-sm text-gray-700 whitespace-pre-wrap">
      {content && content.trim() ? content : <span className="italic text-gray-400">Not provided by the center yet</span>}
    </p>
  </div>
);

const PaymentAgreementFormContent = ({ data, updateField }) => {
  return (
    <div className="space-y-6" data-testid="payment-agreement-form">
      <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50/60 p-4">
        <p className="text-sm text-amber-900">
          <strong>Provider / Parent Written Payment Agreement.</strong> Please review the center's
          policies below (set by the center), then fill in your child's tuition rate and schedule,
          and sign at the bottom.
        </p>
      </div>

      {/* Provider identity (read-only, from center settings) */}
      <div>
        <h3 className="font-semibold mb-2">Provider</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Business / Provider name</Label>
            <Input value={data.provider_name || ''} readOnly className="bg-gray-50" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Location / License #</Label>
            <Input value={data.provider_number || ''} readOnly className="bg-gray-50" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs text-gray-500">Address</Label>
            <Input value={data.provider_address || ''} readOnly className="bg-gray-50" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Phone</Label>
            <Input value={data.provider_phone || ''} readOnly className="bg-gray-50" />
          </div>
        </div>
      </div>

      {/* Parents */}
      <div>
        <h3 className="font-semibold mb-2">Parent(s) / Guardian(s)</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>Parent 1 — Full name *</Label>
            <Input
              placeholder="Last, First, MI"
              value={data.parent_name || ''}
              onChange={(e) => updateField('parent_name', e.target.value)}
              data-testid="pa-parent-name"
            />
          </div>
          <div>
            <Label>Parent 2 — Full name</Label>
            <Input
              placeholder="Last, First, MI"
              value={data.parent2_name || ''}
              onChange={(e) => updateField('parent2_name', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* For the care of */}
      <div>
        <h3 className="font-semibold mb-2">For the Care of</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Child name</Label>
            <Input value={`${data.child_first_name || ''} ${data.child_last_name || ''}`.trim()} readOnly className="bg-gray-50" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Date of birth</Label>
            <Input value={data.child_date_of_birth || ''} readOnly className="bg-gray-50" />
          </div>
          <div>
            <Label>Child care price *</Label>
            <div className="flex gap-2">
              <div className="flex items-center px-2 bg-gray-100 rounded border">$</div>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={data.price_amount || ''}
                onChange={(e) => updateField('price_amount', e.target.value)}
                data-testid="pa-price-amount"
              />
              <select
                className="border rounded px-2 text-sm"
                value={data.price_period || 'month'}
                onChange={(e) => updateField('price_period', e.target.value)}
                data-testid="pa-price-period"
              >
                <option value="month">per month</option>
                <option value="week">per week</option>
                <option value="other">other</option>
              </select>
            </div>
          </div>
          <div>
            <Label>Payment schedule *</Label>
            <div className="flex gap-2">
              <select
                className="border rounded px-2 text-sm"
                value={data.payment_schedule || 'Monthly'}
                onChange={(e) => updateField('payment_schedule', e.target.value)}
                data-testid="pa-schedule"
              >
                <option value="Monthly">Monthly</option>
                <option value="Weekly">Weekly</option>
                <option value="Other">Other</option>
              </select>
              <Input
                placeholder={data.payment_schedule === 'Weekly' ? 'Day of week' : 'Day of month'}
                value={data.payment_schedule_day || ''}
                onChange={(e) => updateField('payment_schedule_day', e.target.value)}
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Agreed upon start date *</Label>
            <Input
              type="date"
              value={data.start_date || ''}
              onChange={(e) => updateField('start_date', e.target.value)}
              data-testid="pa-start-date"
            />
          </div>
        </div>
      </div>

      {/* Center policies — read-only from settings */}
      <div>
        <h3 className="font-semibold mb-2">Center Policies</h3>
        <p className="text-xs text-gray-500 mb-3">
          These policies were set by the center. By signing below you acknowledge them.
        </p>
        <div className="grid gap-2">
          <PolicyReadOnly label="Days & hours of operation" content={data.days_hours_of_operation} />
          <PolicyReadOnly label="Deposits / holding a slot" content={data.deposit_policy} />
          <PolicyReadOnly label="Closure dates & payment policy" content={data.closure_dates_policy} />
          <PolicyReadOnly label="Expected child absences" content={data.expected_absence_policy} />
          <PolicyReadOnly label="Unexpected child absences" content={data.unexpected_absence_policy} />
          <PolicyReadOnly label="Payment dispute policy" content={data.payment_dispute_policy} />
          <PolicyReadOnly label="Termination / expulsion" content={data.termination_policy} />
          <PolicyReadOnly label="Available discounts & scholarships" content={data.available_discounts} />
          <PolicyReadOnly label="Provider fees" content={data.provider_fees} />
          <PolicyReadOnly label="Miscellaneous policies" content={data.misc_policies} />
        </div>
      </div>

      {/* Multi-child: siblings this signature also covers */}
      {Array.isArray(data.siblings) && data.siblings.length > 0 && (
        <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-4" data-testid="pa-siblings-section">
          <div className="flex items-center gap-2 mb-1">
            <Baby className="w-4 h-4 text-primary" />
            <h3 className="font-semibold">Also cover siblings</h3>
          </div>
          <p className="text-xs text-gray-600 mb-3">
            Check any other children enrolled at this center who this signed agreement should also cover.
            One signature can cover the whole family.
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            {data.siblings.map((sib) => {
              const covered = Array.isArray(data.covered_child_ids) ? data.covered_child_ids : [];
              const isChecked = covered.includes(sib.id);
              return (
                <label
                  key={sib.id}
                  className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition ${
                    isChecked ? 'bg-primary/10 border-primary' : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                  data-testid={`pa-sibling-${sib.id}`}
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-primary"
                    checked={isChecked}
                    onChange={(e) => {
                      const next = new Set(covered);
                      if (e.target.checked) next.add(sib.id); else next.delete(sib.id);
                      updateField('covered_child_ids', Array.from(next));
                    }}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{sib.name || 'Sibling'}</div>
                    {sib.date_of_birth && (
                      <div className="text-xs text-gray-500">DOB: {sib.date_of_birth}</div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Attestation + signature */}
      <div className="border-t pt-4">
        <h3 className="font-semibold mb-2">Attestation & Signature</h3>
        <p className="text-sm text-gray-700 mb-3">
          By signing below I agree to abide by this agreement and the written policies of the
          provider. I acknowledge the provider may amend policies by providing notice.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="block mb-2">Parent signature *</Label>
            <SignaturePad
              value={data.parent_signature || ''}
              typedName={data.parent_signature_typed || data.parent_name || ''}
              onTypedNameChange={(v) => updateField('parent_signature_typed', v)}
              onChange={(v) => updateField('parent_signature', v)}
              testId="pa-parent-signature"
            />
          </div>
          <div>
            <Label>Date signed *</Label>
            <Input
              type="date"
              value={data.parent_signature_date || ''}
              onChange={(e) => updateField('parent_signature_date', e.target.value)}
              data-testid="pa-parent-signature-date"
            />
          </div>
        </div>
      </div>
    </div>
  );
};


export default ParentEnrollmentPage;
